'use client';

import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Facebook, Twitter, Linkedin, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState } from 'react';

const WhatsappIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
        <title>WhatsApp</title>
        <path d="M12.04 2.02c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.02 22l5.25-1.38c1.45.79 3.08 1.21 4.78 1.21h.01c5.46 0 9.91-4.45 9.91-9.91s-4.45-9.91-9.91-9.91zm0 18.02c-1.63 0-3.24-.44-4.63-1.28l-.33-.2-3.44.9.92-3.36-.22-.35c-.93-1.48-1.42-3.19-1.42-4.93 0-4.52 3.68-8.19 8.19-8.19 4.52 0 8.19 3.67 8.19 8.19s-3.67 8.19-8.19 8.19zm4.5-5.92c-.25-.12-1.47-.72-1.7-.8-.22-.08-.38-.12-.54.12-.16.25-.64.8-.79.96-.15.16-.3.18-.54.06-.25-.12-1.04-.39-1.98-1.22-.73-.65-1.22-1.46-1.37-1.71-.16-.25-.02-.38.1-.51.11-.11.25-.28.38-.42.12-.14.16-.25.25-.41.08-.16.04-.3-.02-.42-.06-.12-.54-1.29-.74-1.77-.2-.48-.4-.41-.54-.42-.14 0-.3 0-.46 0-.16 0-.42.06-.64.3.22.25-.87 1.03-.87 2.5 0 1.47.9 2.9,1.03 3.1.12.25 1.75 2.67 4.25 3.75 2.5 1.08 2.5.72 2.94.69.44-.03 1.47-.6 1.68-1.18.2-.58.2-1.08.14-1.18-.06-.1-.22-.16-.47-.28z"/>
    </svg>
);


export function SocialShareButtons({ title }: { title: string }) {
    const pathname = usePathname();
    const { toast } = useToast();
    const [url, setUrl] = useState('');

    useEffect(() => {
        setUrl(window.location.origin + pathname);
    }, [pathname]);

    if (!url) {
        return null;
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(url);
        toast({
            title: "Copied to clipboard!",
            description: "The link has been copied to your clipboard.",
        });
    };

    return (
        <div className="flex items-center gap-2 mt-8 py-8 border-t">
            <span className="text-sm font-semibold">Share this post:</span>
            <Button variant="outline" size="icon" asChild>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
                    <Facebook />
                </a>
            </Button>
            <Button variant="outline" size="icon" asChild>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter">
                    <Twitter />
                </a>
            </Button>
            <Button variant="outline" size="icon" asChild>
                <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn">
                    <Linkedin />
                </a>
            </Button>
             <Button variant="outline" size="icon" asChild>
                <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(title + " " + url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp">
                    <WhatsappIcon className="h-4 w-4" />
                </a>
            </Button>
            <Button variant="outline" size="icon" onClick={copyToClipboard} aria-label="Copy link">
                <Copy />
            </Button>
        </div>
    );
}
